import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { Goal } from '@feathr/blackbox';
import { ArchiveModal, toast } from '@feathr/components';
import { errorMessage, logError } from '@feathr/hooks';

interface IArchiveGoalModalProps {
  goal: Goal;
  onClose: () => void;
  opened: boolean;
}

function ArchiveGoalModal({
  goal,
  onClose,
  opened,
}: Readonly<IArchiveGoalModalProps>): JSX.Element {
  const { t } = useTranslation();
  const name = goal.get('name');

  async function archive(): Promise<void> {
    try {
      // Patch rather than goal.archive(), which removes the model and would drop it from the Archived tab.
      await goal.patch({ is_archived: true });
      // rachis's patch sets isErrored/error rather than rejecting
      if (goal.isErrored) {
        throw goal.error;
      }
      toast(t("Archived '{{- name}}'.", { name }), { type: 'success' });
      onClose();
      goal.collection?.refreshApiCache();
    } catch (error) {
      logError({
        extra: { goalId: goal.id, message: errorMessage(error, t) },
        level: 'error',
        message: 'Failed to archive goal',
      });
      toast(t("Couldn't archive goal. Try again."), { type: 'error' });
    }
  }

  return (
    <ArchiveModal
      description={t(
        "Archiving removes this goal from all reports going forward. Conversions continue to be tracked in the background, and historical data is preserved. New campaigns and flights won't auto-inherit it. You can unarchive anytime.",
      )}
      onClose={onClose}
      onConfirm={archive}
      opened={opened}
      t={t}
      title={t("Archive '{{- name}}'?", { name })}
    >
      {false}
    </ArchiveModal>
  );
}

export default ArchiveGoalModal;
